// AWP Review — work packages. Mirrors askSmart-backend/src/utils/verity/packageStore.ts.

export type PackageType = "CWP" | "EWP" | "PWP";
export const PACKAGE_TYPES: PackageType[] = ["CWP", "EWP", "PWP"];

export const PACKAGE_TYPE_LABELS: Record<PackageType, string> = {
  CWP: "Construction Work Package",
  EWP: "Engineering Work Package",
  PWP: "Procurement Work Package",
};

/** The approved discipline list — the server rejects anything else. */
export const DISCIPLINES = [
  "Civil",
  "Structural",
  "Piping",
  "Mechanical",
  "Electrical",
  "Instrumentation",
  "Other",
] as const;

/** A Construction Work Area, held on the project and picked from on every package. */
export interface WorkArea {
  id: string;
  code: string;
  name?: string;
  sequence?: number;
}

export interface WorkPackage {
  id: string;
  orgID: string;
  projectId: string;
  type: PackageType;
  packageNo: string;
  title: string;
  discipline?: string;
  revision?: string;
  cwaId?: string;
  hours?: number;
  plannedStart?: string;
  plannedFinish?: string;
  needDate?: string;
  scheduleActivityIds?: string[];
  contractor?: string;
  owner?: string;
  scope?: string;
  boundaries?: string;
  /** CWP only — the EWPs / PWPs linked to it. The only place these links are stored. */
  ewpIds?: string[];
  pwpIds?: string[];
  /** EWP only — the PWPs it feeds. */
  feedsPwpIds?: string[];
  procurementCategory?: string;
  rosDate?: string;
  longLead?: boolean;
  /** The document the package was created from, when it arrived by upload. */
  source?: { fileName: string; blobName: string; fileSize: number; contentType: string; typeFrom?: string };
  /** Set once the Documents stage has read the document. The receipt only — see PackageDocument. */
  read?: {
    at: string;
    by?: string;
    fileName: string;
    blobName: string;
    version: number;
    counts: PackageReadCounts;
    warnings: string[];
  };
  createdAt: string;
  updatedAt?: string;
  _etag?: string;
}

export interface PackageReadCounts {
  sections: number;
  tables: number;
  materials: number;
  drawings: number;
  holds: number;
  references: number;
}

/** What the package form sends. The server mints id, orgID, projectId and the timestamps. */
export type PackageDraft = Omit<
  WorkPackage,
  "id" | "orgID" | "projectId" | "createdAt" | "updatedAt" | "_etag"
> & { _etag?: string };

// ─── What the Documents stage reads out of a package document ───────────────────
//
// Mirrors askSmart-backend/src/utils/verity/packageDocument.ts. A package is almost entirely
// tables, and the AWP rules compare those tables ACROSS packages — so what is read is rows, with
// a pointer back to the table and row each fact came from.

export type PackageTableKind =
  | "control"
  | "revisions"
  | "linkedPackages"
  | "holds"
  | "drawings"
  | "bom"
  | "requiredAtSite"
  | "procurement"
  | "milestones"
  | "checklist"
  | "other";

/** What each kind of table is called on screen, and whether the review reads it. */
export const TABLE_KINDS: Record<PackageTableKind, { label: string; used: boolean }> = {
  control: { label: "Cover block", used: true },
  revisions: { label: "Revision history", used: false },
  linkedPackages: { label: "Linked packages", used: true },
  holds: { label: "Holds", used: true },
  drawings: { label: "Drawing register", used: true },
  bom: { label: "Bill of materials", used: true },
  requiredAtSite: { label: "Required at site", used: true },
  procurement: { label: "Material list", used: true },
  milestones: { label: "Milestones", used: false },
  checklist: { label: "Checklist", used: false },
  other: { label: "Not recognised", used: false },
};

export interface PackageTable {
  id: string;
  kind: PackageTableKind;
  heading: string;
  columns: string[];
  rows: string[][];
}

export interface Evidence {
  /** Empty when the fact came from a bullet list rather than a table. */
  tableId: string;
  heading: string;
  row: number;
}

/** A date as the document wrote it; `iso` is absent when the form was ambiguous. */
export interface DateValue {
  text: string;
  iso?: string;
}

export interface PackageReference {
  packageNo: string;
  type?: PackageType;
  revision?: string;
  role?: string;
  evidence: Evidence;
}

export interface HoldLine {
  holdId?: string;
  description: string;
  responsible?: string;
  targetClose?: DateValue;
  gate?: string;
  status?: string;
  evidence: Evidence;
}

export interface DrawingLine {
  number: string;
  revision?: string;
  title?: string;
  evidence: Evidence;
}

export interface MaterialLine {
  description: string;
  identCode?: string;
  tag?: string;
  discipline?: string;
  quantity?: number;
  quantityText?: string;
  unit?: string;
  pwpNo?: string;
  vendor?: string;
  poNo?: string;
  poIssue?: DateValue;
  poDelivery?: DateValue;
  requiredAtSite?: DateValue;
  evidence: Evidence;
}

export interface PackageCover {
  project?: string;
  projectCode?: string;
  packageNo?: string;
  cwa?: string;
  discipline?: string;
  revision?: string;
  status?: string;
  issueDate?: DateValue;
  owner?: string;
  classification?: string;
}

export interface PackageFacts {
  cover: PackageCover;
  references: PackageReference[];
  holds: HoldLine[];
  drawings: DrawingLine[];
  materials: MaterialLine[];
}

export interface PackageSection {
  id: string;
  title: string;
  level: number;
  text: string;
  bullets: string[];
  tableIds: string[];
}

export interface PackageDocument {
  version: number;
  readAt: string;
  fileName: string;
  sections: PackageSection[];
  tables: PackageTable[];
  facts: PackageFacts;
  warnings: string[];
}

// ─── Review Engine ──────────────────────────────────────────────────────────────
//
// Mirrors askSmart-backend/src/utils/verity/awpReview.ts. Every AWP finding is a comparison
// between two documents, so it always carries both sides and is always certain — there is no
// confidence score here, because nothing was judged.

export type AwpSeverity = "Critical" | "Major" | "Minor";
export type AwpFindingStatus = "open" | "accepted" | "false-positive" | "waived";

export type AwpRuleId =
  | "cwp-ewp-revision"
  | "cwp-pwp-required-date"
  | "bom-quantity"
  | "ewp-cwp-drawing-revision"
  | "hold-after-gate"
  | "po-delivery-late"
  | "unknown-package-reference"
  | "unique-identifier"
  | "cover-revision-control"
  | "cwa-assigned"
  | "quantity-uom-populated"
  | "po-attributes";

export interface AwpSide {
  packageId?: string;
  packageNo: string;
  value: string;
  evidence?: Evidence;
}

export interface AwpFinding {
  id: string;
  ruleId: AwpRuleId;
  /** The standard's rule codes this breaches, when the run was made against a standard. */
  ruleCodes?: string[];
  title: string;
  category: string;
  severity: AwpSeverity;
  detail: string;
  packageId: string;
  packageNo: string;
  packageType: PackageType;
  evidence: Evidence;
  found: AwpSide;
  against: AwpSide[];
  status?: AwpFindingStatus;
  reviewNote?: string;
  reviewedAt?: string;
  reviewedBy?: string;
}

export interface AwpRuleResult {
  id: AwpRuleId;
  title: string;
  severity: AwpSeverity;
  compared: number;
  raised: number;
  /** Set when the rule had nothing to compare — the honest denominator beside a clean result. */
  skipped?: string;
}

// ─── The standard the run was made against ──────────────────────────────────────
//
// A run reports on EVERY rule of the chosen standard, not only the ones it could decide. The five
// statuses exist so that "not-automated" can never be read as a pass: a rule nobody implemented
// was not examined, and it sits in the same list, at the same size, as the ones that ran.

export type CatalogueRuleStatus =
  | "pass"
  | "findings"
  | "no-data"
  | "not-applicable"
  | "not-automated";

export const RULE_STATUS_LABELS: Record<CatalogueRuleStatus, string> = {
  pass: "Pass",
  findings: "Findings",
  "no-data": "Nothing to test",
  "not-applicable": "Not applicable",
  "not-automated": "No automated check",
};

export interface CatalogueRuleResult {
  /** Unique across standards: `${ruleSetId}:${code}` — two standards can share a code. */
  id: string;
  ruleSetId: string;
  ruleSetDocNo?: string;
  code: string;
  title: string;
  requirement: string;
  domain: string;
  domainCode: string;
  gate: string;
  severity: string;
  packageTypes: PackageType[];
  status: CatalogueRuleStatus;
  checks: AwpRuleId[];
  compared: number;
  raised: number;
  reason?: string;
}

export interface AwpRunCoverage {
  total: number;
  automated: number;
  passed: number;
  failed: number;
  noData: number;
  notApplicable: number;
  notAutomated: number;
}

export interface AwpRun {
  runAt: string;
  runBy?: string;
  startedAt?: string;
  durationMs?: number;
  packageIds: string[];
  excluded: { packageNo: string; reason: string }[];
  rules: AwpRuleResult[];
  findings: AwpFinding[];
  ruleSets?: { id: string; title: string; docNo?: string; version?: string; layer?: string }[];
  ruleResults?: CatalogueRuleResult[];
  coverage?: AwpRunCoverage;
}

/** A finding with no decision on it reads as open. */
export const statusOf = (f: AwpFinding): AwpFindingStatus => f.status ?? "open";

export const STATUS_LABELS: Record<AwpFindingStatus, string> = {
  open: "Open",
  accepted: "Accepted",
  "false-positive": "False positive",
  waived: "Waived",
};

/** Where a package stands in the Documents stage. Derived, never stored. */
export type DocumentState = "none" | "unread" | "read" | "stale";

export function documentStateOf(pkg: WorkPackage, extractorVersion: number): DocumentState {
  if (!pkg.source) return "none";
  if (!pkg.read) return "unread";
  // A document replaced since it was read, or read by an older reader, is not a current read.
  if (pkg.read.fileName !== pkg.source.fileName) return "unread";
  return pkg.read.version === extractorVersion ? "read" : "stale";
}

/**
 * Whether a package has been placed in the plan — the status the register shows.
 *
 * DERIVED, NEVER SET. Placed means it has a work area and is linked: a CWP to at least one EWP or
 * PWP, an EWP or PWP onto at least one CWP. Editing a package's details does not move it; only its
 * plan does. The same idea as an engineering project's status falling out of its pipeline.
 */
export function isPlaced(pkg: WorkPackage, all: WorkPackage[]): boolean {
  if (!pkg.cwaId) return false;
  if (pkg.type === "CWP") return (pkg.ewpIds ?? []).length + (pkg.pwpIds ?? []).length > 0;
  return cwpsLinking(pkg, all).length > 0;
}

/** The CWPs that list this package — the derived other end of a CWP's ewpIds / pwpIds. */
export function cwpsLinking(pkg: WorkPackage, all: WorkPackage[]): WorkPackage[] {
  if (pkg.type === "CWP") return [];
  return all.filter(
    (p) =>
      p.type === "CWP" &&
      (pkg.type === "EWP" ? p.ewpIds ?? [] : p.pwpIds ?? []).includes(pkg.id),
  );
}
